import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Label, Input, Table } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { fetchPickListDetailsAutoComplete } from '../../../store/apis/autocompleteService';
import { fetchHRCodeList } from '../../../store/apis/commonService';
import { FileUtil } from '../../../utils';
import { useTicketDetails } from '../../../pages/Tickets/context/TicketDetailsProvider';
import dlIcon from '../../../assets/icons/dlicon.png';
import eyeIcon from '../../../assets/icons/eyeicon.png';
import { getAuthUser } from '../../../utils/auth.util';

export default function TicketDetailView() {
  const {
    ticketDetails,
    downloadAttachmentMutation,
    setDisplayModalOpen,
    setDisplayModalFile,
  } = useTicketDetails();

  const { data: ticketCategoryList } = useQuery({
    queryKey: ['ticketCategory'],
    queryFn: async () => {
      const ticketCategoryData = await fetchHRCodeList({
        type: 'HR_TICKET_CAT',
        param1: getAuthUser()?.hrCoCd.trim(),
      });
      return ticketCategoryData.map(item => ({
        value: item.Description?.trim(),
        label: item.Description?.trim(),
      }));
    },
    enabled: !!getAuthUser()?.hrCoCd,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
  });

  const handleTicketDownloadAttachment = async item => {
    try {
      const response = await downloadAttachmentMutation.mutateAsync({
        ticketNo: ticketDetails?.TicketNo,
        fileName: item,
        type: 'ticket',
      });
      const blob = new Blob([response], { type: response.type });
      FileUtil.downloadBlobFile(
        blob,
        FileUtil.parseFileName(item),
        response.type,
      );
    } catch (error) {
      toast.error('Failed to download attachment. Please try again.', {
        className: 'toast-error',
      });
    }
  };

  const handleTicketDisplayAttachment = async item => {
    try {
      const response = await downloadAttachmentMutation.mutateAsync({
        ticketNo: ticketDetails?.TicketNo,
        fileName: item,
        type: 'ticket',
      });
      const blob = new Blob([response], { type: response.type });
      const file = new File([blob], FileUtil.parseFileName(item), {
        type: response.type,
      });
      setDisplayModalFile(file);
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error('Failed to download attachment. Please try again.', {
        className: 'toast-error',
      });
    }
  };

  const renderAttachments = () => {
    if (
      !ticketDetails?.DocumentNames ||
      ticketDetails?.DocumentNames.length === 0
    ) {
      return (
        <tr>
          <td colSpan="3" className="text-center py-2">
            No attachments available.
          </td>
        </tr>
      );
    }
    return ticketDetails?.DocumentNames?.map((item, index) => (
      <tr key={item.id}>
        <td className="py-2">{index + 1}</td>
        <td className="py-2">{FileUtil.parseFileName(item)}</td>
        <td className="py-2">
          <button
            title="View Document"
            type="button"
            className="btn btn-link p-0"
            onClick={() => handleTicketDisplayAttachment(item)}
          >
            <img src={eyeIcon} alt="View" style={{ height: '15px' }} />
          </button>
          <button
            title="Download Document"
            type="button"
            className="ms-2 btn btn-link p-0"
            onClick={() => handleTicketDownloadAttachment(item)}
          >
            <img src={dlIcon} alt="Download" style={{ height: '15px' }} />
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div className="d-flex flex-column gap-3 p-3">
      {/* Category */}
      <div>
        <Label>Category</Label>
        <Input
          disabled
          className="form-select external-filter-input"
          type="select"
          value={ticketDetails?.Category}
        >
          <option value="">Select Category</option>
          {ticketCategoryList &&
            ticketCategoryList?.map((category, index) => (
              <option key={index} value={category.value}>
                {category.label}
              </option>
            ))}
        </Input>
      </div>
      {/* HR Responsible */}
      <div>
        <Label>HR Responsible</Label>
        <AsyncSelect
          cacheOptions
          defaultOptions
          loadOptions={async input =>
            (
              await fetchPickListDetailsAutoComplete({
                prefixText: input,
                type: 'USER',
              })
            ).map(item => ({ value: item.ID, label: item.Name }))
          }
          classNamePrefix="select"
          isClearable
          placeholder="Search HR Responsible"
          value={{
            label: ticketDetails?.AssignedTo,
            value: ticketDetails?.AssignedToId,
          }}
          isDisabled
          styles={{
            control: base => ({
              ...base,
              border: '1px solid #929398',
              borderRadius: '8px',
              backgroundColor: '#EFF2F7',
            }),
            valueContainer: base => ({
              ...base,
              cursor: 'text',
            }),
            singleValue: (base, state) => ({
              ...base,
              color: state.isDisabled ? '#212529' : base.color,
              opacity: 1,
            }),
          }}
          noOptionsMessage={() => null}
        />
      </div>
      {/* Subject */}
      <div>
        <Label>Subject</Label>
        <Input
          disabled
          className="form-control external-filter-input"
          placeholder="Enter Subject"
          value={ticketDetails?.Subject || ''}
        />
      </div>
      {/* Message */}
      <div>
        <Label>Message</Label>
        <Input
          disabled
          placeholder="Enter Message"
          className="form-control external-filter-input"
          rows="4.8"
          type="textarea"
          value={ticketDetails?.Description || ''}
        />
      </div>
      {/* Attachment Table */}
      <div className="ticket-attachment-table-outer">
        <Table className="ticket-attachment-table">
          <thead>
            <tr>
              <th className="py-2 ps-2 fw-semibold">#</th>
              <th className="py-2 fw-semibold">Attachment Path</th>
              <th className="py-2 fw-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>{renderAttachments()}</tbody>
        </Table>
      </div>
    </div>
  );
}
